/*
 * Copyright (c) 2018. Unconventional Software Corporation
 */

import {ScheduleItem} from "./ScheduleItem";
import {EntryScheduleItem} from "./EntryScheduleItem";
import {CompetitionMetadata} from "./metadata/CompetitionMetadata";
import {GeneralEntryItem} from "./GeneralEntryItem";
import * as moment from 'moment';


export class ScheduleBuilder {

  private static readonly DURATION_MINUTE = 'm';
  private static readonly DEFAULT_START_TIME = '08:00:00';

  static fromAngularFireSnapshots(entrySnapshots: any, metadata: CompetitionMetadata, calculateSchedule: boolean) {

    let schedule: ScheduleItem[] = entrySnapshots
      .map(snap => this._fromAngularFireSnapshot(snap.key, snap.payload.val(), metadata))
      .sort((s1, s2) => s1.sequence - s2.sequence);

    if (calculateSchedule) {
      return this._calculateSchedule(schedule);
    }

    return schedule;
  }

  // region Item Builders

  private static _fromAngularFireSnapshot(key: string, payload: any, metadata: CompetitionMetadata) : ScheduleItem {
    switch (payload.routineType) {
      case ScheduleItem.ENTRY_TYPE_ROUTINE:
        return this._fromRoutineSnapshot(key, payload, metadata);
      default:
        return this._fromGeneralSnapshot(key, payload);
    }
  }

  private static _fromRoutineSnapshot(key: string, payload: any, metadata: CompetitionMetadata) : EntryScheduleItem {
    let entry: EntryScheduleItem = EntryScheduleItem.fromJson(key, payload);

    if (entry && metadata) {
      entry.ageCategory = metadata.getAgeCategoryById(entry.ageCategory.id);
      entry.category = metadata.getCategoryById(entry.category.id);
      entry.classification = metadata.getClassficationById(entry.classification.id);
      entry.entryType = metadata.getEntryTypeById(entry.entryType.id);
    }

    return entry;
  }

  private static _fromGeneralSnapshot(key: string, payload: any) : GeneralEntryItem {
    return GeneralEntryItem.fromJson(key, payload);
  }

  // endregion

  private static _sortBySequence() {

  }

  private static _calculateSchedule(items: ScheduleItem[]) : ScheduleItem[] {

    let previousSequence = -9999; // TODO: Remove (debugging)

    let lastEndTime: moment.Moment = undefined;

    for (let i = 0; i < items.length; i++) {
      // region Debugging : TODO: remove
      if (items[i].sequence < previousSequence) {
        console.warn('Entries are out of order!');
      }
      previousSequence = items[i].sequence;
      // endregion

      // do not calculate times is the status is not calculable
      if (this._shouldSkipTime(items[i].entryStatus)) {
        items[i].formattedTime = '';
        continue;
      }

      // check if the day changed
      if (lastEndTime && items[i].date != lastEndTime.format(ScheduleItem.ENTRY_DATE_FORMAT)) {
        lastEndTime = null;
      }

      let timeResult: TimeSchedule = null;

      switch (items[i].fixedType) {
        case ScheduleItem.TIME_TYPE_FIXED_START:
        case ScheduleItem.TIME_TYPE_AUTO_PLAYED:
          timeResult = this._getFixedStartTimeDetails(items[i], lastEndTime);
          break;
        case ScheduleItem.TIME_TYPE_FIXED_END:
          timeResult = this._getFixedEndTimeDetails(items[i], lastEndTime);
          break;
        default:
          timeResult = this._getUnfixedTimeDetails(items[i], lastEndTime);
          break;
      }

      items[i].formattedTime = timeResult.formattedStartTime;
      lastEndTime = moment(timeResult.endTime);
    }

    return items;
  }

  private static _getFormattedTime(time: any) {
    return moment(time).format('h:mmA')
  }


  private static _shouldSkipTime(entryStatus: number) : boolean {

    if (entryStatus < ScheduleItem.ENTRY_STATUS_PLANNING) {
      return true;
    }

    switch (entryStatus) {
      case ScheduleItem.ENTRY_STATUS_PLANNING:
      case ScheduleItem.ENTRY_STATUS_CANCELLED:
      case ScheduleItem.ENTRY_STATUS_ON_HOLD:
        return true;
      default:
        return false;
    }

  }

  private static _getFixedStartTimeDetails(item: ScheduleItem, previousTime: moment.Moment) : TimeSchedule {

    if (!item.timeSlot || !item.date) {
      return this._getUnfixedTimeDetails(item, previousTime);
    }

    let fixedStart = moment(`${item.date} ${item.timeSlot}`);

    return {
      formattedStartTime: this._getFormattedTime(fixedStart),
      endTime: fixedStart.add(item.duration, this.DURATION_MINUTE)
    };
  }

  private static _getFixedEndTimeDetails(item: ScheduleItem, previousTime: moment.Moment) : TimeSchedule {

    if (!item.timeSlot || !item.date) {
      return this._getUnfixedTimeDetails(item, previousTime);
    }

    let fixedEnd = moment(`${item.date} ${item.timeSlot}`);

    if (previousTime) {

      // check for minimum buffer
      if (fixedEnd.diff(previousTime, this.DURATION_MINUTE) < item.duration) {
        fixedEnd = moment(previousTime).add(item.duration, this.DURATION_MINUTE);
      }

      return {
        formattedStartTime: this._getFormattedTime(previousTime),
        endTime: fixedEnd
      };

    } else {
      // no previous time set so we just use the duration for the start
      return {
        formattedStartTime: this._getFormattedTime(fixedEnd.subtract(item.duration, this.DURATION_MINUTE)),
        endTime: fixedEnd
      };
    }

  }

  private static _getUnfixedTimeDetails(item: ScheduleItem, previousTime: moment.Moment) : TimeSchedule {

    if (previousTime && previousTime.isValid()) {
      return {
        formattedStartTime: this._getFormattedTime(previousTime),
        endTime: previousTime.add(item.duration, this.DURATION_MINUTE)
      };
    }

    // a previous time should have been set, but handle in case it hasn't
    let date = item.date;
    if (!date) {
      console.warn(`No date set for entry ${item.id}`);
      date = moment().format(ScheduleItem.ENTRY_DATE_FORMAT);
    }

    let startTime = moment(`${date} ${this.DEFAULT_START_TIME}`);

    return {
      formattedStartTime: this._getFormattedTime(startTime),
      endTime: startTime.add(item.duration, this.DURATION_MINUTE)
    };
  }

  constructor(private competitionMetadata: CompetitionMetadata) {}

}

export interface TimeSchedule {
  formattedStartTime: string,
  endTime: moment.Moment
}



// WEBPACK FOOTER //
// ./src/models/schedule/ScheduleBuilder.ts